"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appointmentEditRecordQuerySchema = exports.appointmentEditPendingQuerySchema = exports.requestAppointmentEditSchema = exports.previewAppointmentEditSchema = exports.updateAppointmentRequestSchema = exports.updateAppointmentDraftSchema = exports.appointmentPayerContextEnum = exports.appointmentConsultationModeEnum = exports.appointmentPriorityEnum = void 0;
/**
 * APPOINTMENT EDIT REQUEST (APPTEDIT, desk 2026-10-18: "next will go to appointment" - the same promise as the patient edit: whatever the
 * desk typed when booking can be corrected, through Request -> Verify -> Approve, by the user who made the booking).
 *
 * THE BODY MIRRORS WHAT BOOKING TAKES: the appointment, the lock version the clerk was looking at, and ONLY the fields that differ (absent =
 * leave alone, `null` = clear, where a field can be empty). A reason is required - it is what the verifier and approver read. Nothing on the
 * appointment changes until approval.
 *
 * WHAT IS NOT HERE, AND WHY:
 *   - the status moves (confirm / check-in / no-show / complete) and Reschedule / Cancel stay direct actions on the Lookup tab: they are the
 *     desk's daily operation, not corrections;
 *   - "booking type" (Standard / Corporate / Telemedicine / Package / Referral ...) is not a column, it is the endpoint a booking took; what
 *     it stored (payer context, consultation mode, tariff, package, referral, priority) is what is editable;
 *   - the follow-up link and the fee waiver of a revisit (derived by the server at booking, they decide money);
 *   - a same-day visit's date: it takes no slot or number, so it cannot become a future reservation (or the reverse) by an edit.
 */
const zod_1 = require("zod");
const clearableId = zod_1.z.string().uuid().nullable().optional();
const PHONE_PATTERN = /^\+?[0-9 ()-]{5,20}$/;
/** VIP / Emergency are the two ticks on the booking form; the server turns them into the queue priority tier (0 / 50 / 100). */
exports.appointmentPriorityEnum = zod_1.z.enum(['NORMAL', 'VIP', 'EMERGENCY']);
exports.appointmentConsultationModeEnum = zod_1.z.enum(['IN_PERSON', 'TELEMEDICINE', 'HOME_VISIT']);
exports.appointmentPayerContextEnum = zod_1.z.enum(['GENERAL', 'CORPORATE', 'INSURANCE', 'PACKAGE']);
/** A caller who rang before being registered: the draft the booking is held under. */
exports.updateAppointmentDraftSchema = zod_1.z.object({
    givenName: zod_1.z.string().trim().min(1).max(150),
    familyName: zod_1.z.string().trim().max(150).nullable().optional(),
    mobile: zod_1.z.string().trim().regex(PHONE_PATTERN, 'Enter a valid phone number'),
});
exports.updateAppointmentRequestSchema = zod_1.z.object({
    appointmentId: zod_1.z.string().uuid(),
    expectedLockVersion: zod_1.z.number().int().nonnegative(),
    /** The patient the booking is for. Refused when a receipt exists (the receipt's own OP-number correction moves both). */
    patientId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    /** A USER id - `Appointment.doctorId` is a User FK, which is what `DoctorDto.userId` carries. */
    doctorId: zod_1.z.string().uuid().optional(),
    /** The slot's date and time; the day it falls on is the slot's day, exactly as at booking. */
    scheduledAt: zod_1.z.coerce.date().optional(),
    /** The doctor's numbered slot (the queue position). */
    slotNumber: zod_1.z.number().int().min(1).max(500).nullable().optional(),
    durationMinutes: zod_1.z.number().int().min(5).max(480).optional(),
    bookingChannelId: zod_1.z.string().uuid().optional(),
    consultationMode: exports.appointmentConsultationModeEnum.optional(),
    clinicalPurposeId: clearableId,
    payerContext: exports.appointmentPayerContextEnum.optional(),
    finTariffId: clearableId,
    finPackageId: clearableId,
    patientReferralId: clearableId,
    priority: exports.appointmentPriorityEnum.optional(),
    draft: exports.updateAppointmentDraftSchema.optional(),
    /** Why the appointment was corrected. */
    reason: zod_1.z.string().trim().max(500).optional(),
});
/** What the preview takes: the request without the reason (the clerk checks the change before writing why). */
exports.previewAppointmentEditSchema = exports.updateAppointmentRequestSchema.omit({ reason: true });
exports.requestAppointmentEditSchema = exports.updateAppointmentRequestSchema.extend({
    reason: zod_1.z.string().trim().min(3, 'A reason is required').max(500),
});
exports.appointmentEditPendingQuerySchema = zod_1.z.object({ appointmentId: zod_1.z.string().uuid() });
/** "What is on file for this appointment, and what may be changed?" - the dialog's seed. */
exports.appointmentEditRecordQuerySchema = zod_1.z.object({ appointmentId: zod_1.z.string().uuid() });
//# sourceMappingURL=financial-appointment-correction.request.js.map